import { setDevDebug, state } from "../state";
import type { ScreenContext } from "../app";
import { renderHeader } from "../components";
import { devTuning, resetDevTuning } from "../../devTuning";
import { audioAvailable, previewVolume } from "../../audio";
import { resetSettings, settings, updateSettings, type ZoomLevel } from "../../settings";

// Options pane (Don's direction: "we need a Save and Return to Menu as
// well as Options buttons"), rebuilt in 16.69 around settings that are
// actually backed by real systems rather than a shell that named
// everything as undesigned.
//
// Every control here changes something real: Audio drives the new
// procedural sound layer (src/audio.ts), Graphics toggles 16.59's
// FxEffect layer and picks which of Section 4b's locked ZOOM_RADII the
// Expedition View opens at, and DEV DEBUG is the same flag it always
// was -- now persisted (src/settings.ts) instead of resetting on reload.
//
// **Controls stays genuinely undesigned** and is still listed as such:
// the game is tablet-first and tap-driven (Section 2a), with no
// keybinding layer to rebind, so a Controls section would be inventing a
// system rather than exposing one.
//
// Settings live in their own `starforge_settings` localStorage key, NOT
// in GameSaveData -- they're device preferences, so they survive New Game
// and never travel between devices via the cloud save.
export function renderOptionsScreen(container: HTMLElement, ctx: ScreenContext): void {
  renderHeader(container, ctx);

  const wrap = document.createElement("div");
  container.appendChild(wrap);

  function render(): void {
    const pct = (v: number) => `${Math.round(v * 100)}%`;
    wrap.innerHTML = `
      <h1>Options</h1>
      <p class="dim">
        Saved to this browser/device, separately from your captain -- these survive New Game
        and never sync to the cloud save.
      </p>

      <h2>Audio</h2>
      ${
        audioAvailable()
          ? ""
          : `<p class="dim">This browser has no Web Audio support -- these controls will have no effect here.</p>`
      }
      <div class="cargo-list">
        <div class="cargo-row">
          <label>
            <input type="checkbox" data-opt-muted ${settings.muted ? "checked" : ""} />
            <span class="cargo-row-name">Mute all audio</span>
          </label>
          <span class="dim">Silences everything without losing your volume levels.</span>
        </div>
        <div class="cargo-row">
          <label><span class="cargo-row-name">Master volume</span></label>
          <input type="range" data-opt-vol="masterVolume" min="0" max="1" step="0.05"
            value="${settings.masterVolume}" ${settings.muted ? "disabled" : ""} />
          <span class="dim">${pct(settings.masterVolume)}</span>
        </div>
        <div class="cargo-row">
          <label><span class="cargo-row-name">Effects volume</span></label>
          <input type="range" data-opt-vol="sfxVolume" min="0" max="1" step="0.05"
            value="${settings.sfxVolume}" ${settings.muted ? "disabled" : ""} />
          <span class="dim">${pct(settings.sfxVolume)} -- weapons, mining, repairs, alerts</span>
        </div>
      </div>

      <h2>Graphics</h2>
      <div class="cargo-list">
        <div class="cargo-row">
          <label>
            <input type="checkbox" data-opt-fx ${settings.showCombatFx ? "checked" : ""} />
            <span class="cargo-row-name">Combat &amp; mining effects</span>
          </label>
          <span class="dim">Weapon beams, missile trails, ore particles, repair pulses (16.59). Purely cosmetic -- turning these off changes no damage or yield.</span>
        </div>
        <div class="cargo-row">
          <label><span class="cargo-row-name">Default zoom</span></label>
          <select data-opt-zoom>
            ${(["close", "medium", "far"] as ZoomLevel[])
              .map(
                (z) =>
                  `<option value="${z}" ${settings.defaultZoom === z ? "selected" : ""}>${z[0].toUpperCase()}${z.slice(1)}</option>`
              )
              .join("")}
          </select>
          <span class="dim">Which zoom the Expedition View opens at.</span>
        </div>
      </div>

      <h2>Saves</h2>
      <div class="cargo-list">
        <div class="cargo-row">
          <span class="dim">See both saves' timestamp and location before choosing which to load (16.64).</span>
          <button type="button" data-compare-saves>Compare Saves</button>
        </div>
      </div>

      <h2>Developer</h2>
      <div class="cargo-list">
        <div class="cargo-row">
          <label>
            <input type="checkbox" data-opt-dev ${state.devDebugOn ? "checked" : ""} />
            <span class="cargo-row-name">DEV DEBUG</span>
          </label>
          <span class="dim">Show the full navigation bar and dev-only shortcuts.</span>
        </div>
      </div>

      ${state.devDebugOn ? devTuningHtml() : ""}

      <h2>Not designed yet</h2>
      <div class="cargo-list">
        <div class="cargo-row">
          <span class="dim">Controls -- the game is tap-driven and tablet-first (Section 2a); there is no keybinding layer to rebind.</span>
        </div>
      </div>

      <div class="cargo-list">
        <div class="cargo-row">
          <button type="button" data-opt-reset>Reset settings to defaults</button>
        </div>
      </div>
    `;

    wrap.querySelector<HTMLInputElement>("input[data-opt-dev]")!.addEventListener("change", (e) => {
      setDevDebug((e.target as HTMLInputElement).checked);
      ctx.rerender();
    });

    wrap.querySelector<HTMLInputElement>("input[data-opt-muted]")!.addEventListener("change", (e) => {
      updateSettings({ muted: (e.target as HTMLInputElement).checked });
      render();
      if (!settings.muted) previewVolume();
    });

    wrap.querySelectorAll<HTMLInputElement>("input[data-opt-vol]").forEach((input) => {
      // "input", not "change": the percentage label tracks the drag live.
      // The preview tone fires on release only, so dragging doesn't
      // machine-gun a sound on every step.
      input.addEventListener("input", (e) => {
        const el = e.target as HTMLInputElement;
        updateSettings({ [el.dataset.optVol!]: Number(el.value) });
        render();
      });
      input.addEventListener("change", () => previewVolume());
    });

    wrap.querySelector<HTMLInputElement>("input[data-opt-fx]")!.addEventListener("change", (e) => {
      updateSettings({ showCombatFx: (e.target as HTMLInputElement).checked });
      render();
    });

    wrap.querySelector<HTMLSelectElement>("select[data-opt-zoom]")!.addEventListener("change", (e) => {
      updateSettings({ defaultZoom: (e.target as HTMLSelectElement).value as ZoomLevel });
      render();
    });

    wrap.querySelector<HTMLButtonElement>("[data-opt-reset]")!.addEventListener("click", () => {
      resetSettings();
      state.devDebugOn = settings.devDebugOn;
      ctx.rerender();
    });

    wrap.querySelector<HTMLButtonElement>("[data-compare-saves]")?.addEventListener("click", () => {
      ctx.navigate("saveCompare");
    });

    wrap.querySelector<HTMLInputElement>("input[data-tune=\"fuelBaseCost\"]")?.addEventListener("input", (e) => {
      devTuning.fuelBaseCost = Number((e.target as HTMLInputElement).value);
      render();
    });
    wrap.querySelector<HTMLInputElement>("input[data-tune=\"provisionsPerCrewPerHour\"]")?.addEventListener("input", (e) => {
      devTuning.provisionsPerCrewPerHour = Number((e.target as HTMLInputElement).value);
      render();
    });
    wrap.querySelector<HTMLInputElement>("input[data-tune=\"fuelCapacityScalingExponent\"]")?.addEventListener("input", (e) => {
      devTuning.fuelCapacityScalingExponent = Number((e.target as HTMLInputElement).value);
      render();
    });
    wrap.querySelector<HTMLButtonElement>("[data-tune-reset]")?.addEventListener("click", () => {
      resetDevTuning();
      render();
    });
  }

  render();
}

// Dev-only tuning sliders (16.65) -- live-adjusts src/devTuning.ts's
// constants, which src/fuel.ts and src/provisions.ts already read fresh
// on every call, so a slider drag takes effect immediately, no code
// change or reload needed. Session-only (not persisted) -- see
// devTuning.ts's own header comment.
function devTuningHtml(): string {
  return `
    <h2>Dev Tuning (session-only)</h2>
    <div class="cargo-list">
      <div class="cargo-row">
        <label><span class="cargo-row-name">Fuel consumption rate</span></label>
        <input type="range" data-tune="fuelBaseCost" min="1" max="50" step="1" value="${devTuning.fuelBaseCost}" />
        <span class="dim">${devTuning.fuelBaseCost} fuel/Fast Travel (Small hull base unit, 16.50)</span>
      </div>
      <div class="cargo-row">
        <label><span class="cargo-row-name">Provisions consumption rate</span></label>
        <input type="range" data-tune="provisionsPerCrewPerHour" min="0.01" max="1" step="0.01" value="${devTuning.provisionsPerCrewPerHour}" />
        <span class="dim">${devTuning.provisionsPerCrewPerHour.toFixed(2)} Provisions/crew/hour (Section 11 default: 0.10)</span>
      </div>
      <div class="cargo-row">
        <label><span class="cargo-row-name">Fuel capacity scaling exponent</span></label>
        <input type="range" data-tune="fuelCapacityScalingExponent" min="1" max="2" step="0.05" value="${devTuning.fuelCapacityScalingExponent}" />
        <span class="dim">${devTuning.fuelCapacityScalingExponent.toFixed(2)} (1.0 = same endurance every hull, higher = bigger hulls range further, 16.65)</span>
      </div>
      <div class="cargo-row">
        <button type="button" data-tune-reset>Reset to defaults</button>
      </div>
    </div>
  `;
}
